import React, { Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { RelayEnvironmentProvider, useLazyLoadQuery } from "react-relay";
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigate,
  useNavigationType,
} from "react-router-dom";
import reportWebVitals from "./reportWebVitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import RelayEnvironment from "./relay/RelayEnvironment";

import "./index.scss";

import App from "./pages/App";
import InspectionListContainer from "./pages/InspectionList/InspectionList";
import InspectionMapContainer from "./pages/InspectionMap";
import InspectionDetailsPageContainer, {
  loader as detailsLoader,
} from "./pages/InspectionDetails/InspectionDetailsPage";
import InspectionContainer, {
  loader as inspectionLoader,
} from "./pages/Inspection";
import AccountContainer, { loader as accountLoader } from "./pages/Account";

import Loader from "./components/Fallbacks/Loader";
import Retry from "./components/Fallbacks/Retry";
import ErrorBoundary from "./components/ErrorBoundary";
import AuthProvider from "./pages/Auth";
import { UserAppPermissionsQuery } from "./Queries";
import { QueriesUserAppPermissionsQuery } from "./__generated__/QueriesUserAppPermissionsQuery.graphql";
import { NoAccessContainer } from "./pages/NoAccess";
import { AuthContext } from "./pages/Auth";

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

function AccountRoute() {
  // Keep the old account route but redirect to the new accounts page in SCAN-GIS
  const authstate = React.useContext(AuthContext);

  if (authstate?.profileUrl) {
    window.location.href = authstate.profileUrl;
    return null;
  } else {
    return <AccountContainer />;
  }
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <Retry />,
    children: [
      { index: true, element: <RedirectView /> },
      { path: "no_access", element: <NoAccessContainer /> },
      { path: "lijst", element: <InspectionListContainer /> },
      { path: "kaart", element: <InspectionMapContainer /> },
      {
        path: "b/:id",
        element: <InspectionDetailsPageContainer />,
        loader: detailsLoader,
      },
      {
        path: "s/:id",
        element: <InspectionContainer />,
        loader: inspectionLoader,
      },
      {
        path: "account",
        element: <AccountRoute />,
        loader: accountLoader,
      },
    ],
  },
]);
function RedirectView() {
  const data = useLazyLoadQuery<QueriesUserAppPermissionsQuery>(
    UserAppPermissionsQuery,
    {}
  );

  let navigate = useNavigate();
  useEffect(() => {
    if (data.user?.roles?.totalCount === 0) {
      navigate("/no_access");
    } else {
      navigate("/lijst");
    }
  }, [navigate, data]);

  return null;
}
function getSentryDsnFromCookie() {
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    const [name, value] = cookie.split("=");
    if (name === "sentry") {
      return decodeURIComponent(value);
    }
  }
  return undefined;
}

const dsn = getSentryDsnFromCookie();

if (dsn) {
  Sentry.init({
    dsn,
    integrations: [
      new Sentry.BrowserTracing({
        // See docs for support of different versions of variation of react router
        // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes
        ),
      }),
      new Sentry.Replay(),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 0.1,
    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

root.render(
  <ErrorBoundary>
    <AuthProvider>
      <RelayEnvironmentProvider environment={RelayEnvironment}>
        <Suspense fallback={<Loader />}>
          <RouterProvider router={router} />
        </Suspense>
      </RelayEnvironmentProvider>
    </AuthProvider>
  </ErrorBoundary>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
